<template>
  <section class="starting-point" aria-labelledby="starting-point-heading">
    <div class="starting-point__header">
      <h2 id="starting-point-heading" class="starting-point__title">
        Choose Your Starting Point Today!
      </h2>
      <p class="starting-point__description">
        Select a compact activity kit, explore pre-curated mobile makerspaces,
        or connect with our team for a custom district quote.
      </p>
    </div>

    <div class="starting-point__cards">
      <article v-for="option in options" :key="option.id" class="starting-card">
        <span class="starting-card__badge">{{ option.badge }}</span>
        <div class="starting-card__image">
          <img
            :src="option.image"
            :alt="option.imageAlt"
            class="starting-card__img"
            width="200"
            height="200"
          />
        </div>
        <h3 class="starting-card__title">{{ option.title }}</h3>
        <p class="starting-card__copy">{{ option.description }}</p>
        <ActionButton
          variant="outline"
          size="large"
          full-width
          type="button"
          @click="router.push(option.route)"
        >
          {{ option.buttonText }}
        </ActionButton>
      </article>
    </div>

    <p class="starting-point__note">
      Prices <strong>exclude</strong> applicable taxes and shipping.
    </p>
  </section>
</template>

<script setup>
import { useRouter } from "vue-router";
import ActionButton from "@/components/common/ActionButton.vue";

const router = useRouter();

const options = [
  {
    id: "crate",
    title: "Makercrate",
    description:
      "Ready-to-use activity kits with materials and learning resources for up to 20 students.",
    badge: "Starting at $299",
    image: "/images/maker-cart/get-started/makercrate-kit.png",
    imageAlt: "Makercrate Inventor Kit with classroom materials",
    buttonText: "Explore Makercrate",
    route: { name: "MakerCartPage", query: { tab: "makercrate" } },
  },
  {
    id: "signature",
    title: "Signature Makercart",
    description:
      "A classroom-ready mobile makerspace with four curated activities organized around key learning themes.",
    badge: "Starting at $3,000",
    image: "/images/maker-cart/get-started/signature-makercart.png",
    imageAlt: "Signature Makercart filled with organized classroom supplies",
    buttonText: "Explore Signature Makercarts",
    route: { name: "MakerCartPage", query: { tab: "signature" } },
  },
  {
    id: "custom",
    title: "Custom Makercart",
    description:
      "Choose four or more activities to build a mobile makerspace around your learners and classroom goals.",
    badge: "Starting at $3,200",
    image: "/images/maker-cart/get-started/custom-makercart.png",
    imageAlt: "Custom Makercart with personalized classroom supplies",
    buttonText: "Explore Custom Makercart",
    route: { name: "MakerCartPage", query: { tab: "custom" } },
  },
];
</script>

<style scoped>
.starting-point {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding-top: 16px;
}

.starting-point__header {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  text-align: center;
  width: 100%;
}

.starting-point__title {
  margin: 0;
  width: 100%;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-display-small);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-display-small);
  color: var(--blue-dark-2);
}

.starting-point__description {
  margin: 0;
  width: 100%;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-large);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-title-large);
  color: var(--black-body-text);
}

.starting-point__cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 40px;
  width: 100%;
}

.starting-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  min-height: 530px;
  padding: 24px;
  background-color: var(--white-hover-1);
  border-radius: 24px;
  box-sizing: border-box;
  overflow: hidden;
}

.starting-card__badge {
  position: absolute;
  top: 0;
  right: 0;
  padding: 6px 16px;
  background-color: var(--blue-light-1);
  color: var(--white);
  font-family: var(--font-family-primary);
  font-size: var(--font-size-body-medium);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-body-medium);
  letter-spacing: 0.035px;
  border-radius: 0 24px 0 24px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
  white-space: nowrap;
}

.starting-card__image {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 200px;
  overflow: clip;
}

.starting-card__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.starting-card__title {
  margin: 0;
  width: 100%;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-headline-large);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-headline-large);
  color: var(--primary-blue);
}

.starting-card__copy {
  margin: 0;
  width: 100%;
  min-height: 84px;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-large);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-title-large);
  color: var(--black-body-text);
}

.starting-point__note {
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 10px;
  border-radius: 8px;
  background-color: var(--green-light-1-badge);
  color: var(--primary-green);
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-large);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-title-large);
}

.starting-point__note strong {
  font-weight: var(--font-weight-bold);
}

@media (max-width: 1200px) {
  .starting-point__cards {
    gap: 24px;
  }

  .starting-card {
    min-height: 0;
  }

  .starting-card__title {
    font-size: var(--fs-xxl);
    line-height: 36px;
  }
}

@media (max-width: 992px) {
  .starting-point__cards {
    grid-template-columns: 1fr;
    max-width: 600px;
    margin: 0 auto;
  }

  .starting-card__copy {
    min-height: 0;
  }
}

@media (max-width: 768px) {
  .starting-point {
    gap: 24px;
    padding: 16px 0;
  }

  .starting-point__header {
    gap: 12px;
  }

  .starting-point__title {
    font-size: var(--font-size-title-large);
    line-height: var(--line-height-title-large);
  }

  .starting-point__description {
    font-size: var(--font-size-body-medium);
    line-height: var(--line-height-body-medium);
    letter-spacing: 0.035px;
  }

  .starting-point__cards {
    gap: 40px;
    max-width: none;
    margin: 0;
  }

  .starting-card {
    padding: 24px;
    gap: 16px;
  }

  .starting-card__badge {
    padding: 6px 16px;
    font-size: var(--font-size-body-small);
    line-height: var(--line-height-body-small);
    letter-spacing: 0.048px;
  }

  .starting-card__image {
    width: 180px;
    height: 180px;
  }

  .starting-card__title {
    font-size: var(--font-size-headline-medium);
    line-height: 28px;
  }

  .starting-card__copy {
    min-height: 76px;
    font-size: var(--font-size-title-medium);
    line-height: var(--line-height-title-medium);
    letter-spacing: 0.024px;
  }

  .starting-point__note {
    font-size: var(--font-size-title-medium);
    line-height: var(--line-height-title-medium);
    letter-spacing: 0.024px;
  }
}
</style>
